<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue';

const props = defineProps({
  years: { // X축에 표시될 연도 배열
    type: Array,
    required: true,
    validator: (value) => value.every(year => typeof year === 'number'),
  },
  data: {
    type: Array,
    required: true,
    validator: (value) =>
      value.every(item => item.label && item.color && Array.isArray(item.scores) && item.scores.every(s => s === null || s === undefined || typeof s === 'number')),
  },
  chartType: { 
    type: String,
    required: true,
    validator: (value) => ['literacy', 'numeracy'].includes(value),
  },
  yAxisMin: { // Y축 최솟값
    type: Number,
    default: 1540,
  },
  yAxisMax: { // Y축 최댓값
    type: Number,
    default: 1700,
  },
  yAxisInterval: { // Y축 눈금 간격
    type: Number,
    default: 20,
  },
  chartHeight: { // SVG 영역 높이
    type: String,
    default: '400px',
  },
});

// CSS 변수
const CHART_GLOBAL_PADDING = {
  TOP: 0,
  RIGHT: 275,
  BOTTOM: 100,
  LEFT: 275,
};
const Y_AXIS_LABEL_WIDTH = 60;
const X_AXIS_LABEL_HEIGHT = 30; 

const SVG_VIEW_WIDTH_VIRTUAL = 600;
const SVG_VIEW_HEIGHT_VIRTUAL = 300; 

// SVG 내부 데이터가 그려질 영역의 폭과 높이 (뷰포트 패딩 계산)
const SVG_VIEW_WIDTH = SVG_VIEW_WIDTH_VIRTUAL - Y_AXIS_LABEL_WIDTH - CHART_GLOBAL_PADDING.RIGHT;
const SVG_VIEW_HEIGHT = SVG_VIEW_HEIGHT_VIRTUAL - X_AXIS_LABEL_HEIGHT - CHART_GLOBAL_PADDING.TOP;

const CHART_TRANSLATE_X = 0; 
const CHART_TRANSLATE_Y = 0; 

const yAxisRange = computed(() => props.yAxisMax - props.yAxisMin);

const LITERACY_COLOR = '#6484F0';
const NUMERACY_COLOR = '#F88082'; 
const OUR_SCHOOL_COLOR = '#00A496';

// 그리드 라인 및 Y축 라벨용 퍼센트
const gridPositions = computed(() => {
  const positions = [];
  for (let i = props.yAxisMin; i <= props.yAxisMax; i += props.yAxisInterval) {
    // 0% ~ 100%
    const percent = ((i - props.yAxisMin) / yAxisRange.value) * 100;
    positions.push({ value: i, percent: percent });
  }
  return positions;
});

// X축 연도별 SVG 좌표 계산
const xAxisPoints = computed(() => {
  const points = [];
  const yearCount = props.years.length;
  if (yearCount <= 1) { // 연도가 하나면 중앙 0개면 빈 배열
    if (yearCount === 1) points.push({ year: props.years[0], x: SVG_VIEW_WIDTH / 2, index: 0 });
    return points;
  }

  for (let i = 0; i < yearCount; i++) {
    const x = SVG_VIEW_WIDTH * (i / (yearCount - 1)); // SVG 내부 기준 X 좌표
    points.push({ year: props.years[i], x: x, index: i });
  }
  return points;
});


// 데이터 점수 -> SVG Y 좌표 변환
const getSvgY = (score) => {
  if (score === null || score === undefined || isNaN(score)) {
    return null;
  }
  const scoreHeight = (score - props.yAxisMin) / yAxisRange.value * SVG_VIEW_HEIGHT;
  return SVG_VIEW_HEIGHT - scoreHeight; 
};

// 데이터 연도 -> SVG X 좌표 변환
const getSvgX = (yearIndex) => {
  const yearCount = props.years.length;
  if (yearCount <= 1) return SVG_VIEW_WIDTH / 2; // 연도 하나일 땐 중앙
  return SVG_VIEW_WIDTH * (yearIndex / (yearCount - 1));
};

// 라인 데이터 가공
const processedLineData = computed(() => {
  if (!props.data || !Array.isArray(props.data)) {
    return [];
  }

  return props.data.map(lineItem => {
    let assignedColor;

    if (lineItem.label.includes('우리학교')) {
      assignedColor = OUR_SCHOOL_COLOR; 
    } else if (lineItem.label.includes('표집학교')) {     
      assignedColor = props.chartType === 'literacy' ? LITERACY_COLOR : NUMERACY_COLOR;
    } else {     
      assignedColor = props.chartType === 'literacy' ? LITERACY_COLOR : NUMERACY_COLOR;
    }
    
    const scoresToProcess = Array.isArray(lineItem.scores) ? lineItem.scores : [];

    const processedPoints = scoresToProcess.map((score, index) => {
      const svgX = getSvgX(index);
      const svgY = getSvgY(score);
      if (svgY !== null) {
        return { score, svgX, svgY, yearIndex: index };
      }
      return null;
    }).filter(p => p !== null);

    let pathD = '';
    if (processedPoints.length > 0) {
        pathD += `M ${processedPoints[0].svgX} ${processedPoints[0].svgY}`;
        for (let i = 1; i < processedPoints.length; i++) {
            const prevPoint = processedPoints[i - 1];
            const currentPoint = processedPoints[i];
            
            if (currentPoint.yearIndex !== prevPoint.yearIndex + 1) {
                pathD += ` M ${currentPoint.svgX} ${currentPoint.svgY}`;
            } else {
                pathD += ` L ${currentPoint.svgX} ${currentPoint.svgY}`;
            }
        }
    }
    return {
      ...lineItem,
      color: assignedColor, // 최종 결정된 색상 적용
      pathD: pathD,
      points: processedPoints
    };
  });
});

const legendItems = computed(() => {
  return processedLineData.value;
});

// HTML 라벨을 위한 동적 위치 계산
const svgWrapRef = ref(null); // svg-wrap 엘리먼트 참조
const svgActualWidth = ref(0); // svg-wrap의 실제 렌더링된 너비
const svgActualHeight = ref(0); // svg-wrap의 실제 렌더링된 높이

const updateSvgDimensions = () => {
  if (svgWrapRef.value) {
    const rect = svgWrapRef.value.getBoundingClientRect();
    svgActualWidth.value = rect.width;
    svgActualHeight.value = rect.height; // svg-wrap은 chartHeight prop으로 높이 고정됨
  }
};

onMounted(() => {
  updateSvgDimensions();
  window.addEventListener('resize', updateSvgDimensions);
});

onUnmounted(() => {
  window.removeEventListener('resize', updateSvgDimensions);
});
</script>

<template>
    <div class="trend-line-chart-wrapper">  
        <div class="chart-container">
            <!-- Y축 라벨 -->
            <div class="y-axis-labels" :style="{ height: chartHeight }">
                <p
                    v-for="pos in gridPositions"
                    :key="`y-label-${pos.value}`"
                    class="y-axis-label"
                    :style="{ bottom: pos.percent + '%' }"                   
                >
                    {{ pos.value }}
                </p>
            </div>       
            <div class="chart-wrap" :class="props.chartType">
                <div class="svg-wrap" ref="svgWrapRef" :style="{ height: chartHeight }">     
                    <div class="chart-grid-lines">
                        <div
                        v-for="pos in gridPositions"
                        :key="`grid-${pos.value}`"
                        class="grid-line"
                        :style="{ bottom: pos.percent + '%' }"
                        ></div>
                    </div>

                    <svg
                        :viewBox="`0 0 ${SVG_VIEW_WIDTH} ${SVG_VIEW_HEIGHT}`"
                        class="line-chart-svg"
                    >
                        <g :transform="`translate(${CHART_TRANSLATE_X}, ${CHART_TRANSLATE_Y})`">
                            <!-- 데이터 라인 -->
                            <g v-for="lineItem in processedLineData" :key="lineItem.label">
                                <path
                                  :d="lineItem.pathD"
                                  :stroke="lineItem.color"
                                  stroke-width="2"
                                  fill="none"
                                />
                                <!-- 데이터 포인트 -->
                                <circle
                                  v-for="point in lineItem.points"
                                  :key="`${lineItem.label}-${point.yearIndex}`"
                                  :cx="point.svgX"
                                  :cy="point.svgY"
                                  :fill="lineItem.color"
                                  r="5"
                                  class="data-point"
                                />
                            </g>
                        </g>
                    </svg>        
                </div>     
                <div class="x-axis-labels">
                    <p
                    v-for="point in xAxisPoints"
                    :key="`x-label-${point.year}`"
                    class="x-axis-label"           
                    >
                    {{ point.year }}
                    </p>
                </div>
                <div class="legend-wrap">
                    <div v-for="item in legendItems" :key="item.label" class="legend-item">
                        <p></p>
                        <p> {{ item.label }}</p>
                    </div>
                </div>    
            </div>                 
      </div>  
    </div>
</template>

<style lang="scss" scoped>
.trend-line-chart-wrapper {
  padding: 35px 30px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}

.chart-container {
  position: relative; 
  display: flex;
  gap: 20px; 
  width: 100%;
  min-width: 438px;
}

.svg-wrap {
    position: relative;
}
.line-chart-svg {
  width: 100%; 
  height: 100%; 
  display: block;
  overflow: visible;
}

.y-axis-labels {
    position: relative;
    font-size: 14px;
    line-height: 0;
    width: 40px;
    text-align: center;
}
.y-axis-label {
    position: absolute;
    width: 100%;
    transform: translateY(-50%);
}
.x-axis-labels {
    display: flex;
    font-size: 16px;
    font-weight: 500;
    line-height: 32px;
    justify-content: space-between;
    padding: 0 30px;
    .x-axis-label {
      text-align: center;
    }
}
.chart-grid-lines {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none; 
  z-index: -1; 
}

.grid-line {
  position: absolute;
  left: 0;
  width: 100%;
  height: 0; 
  border-bottom: 2px dashed #E9E9E9;
  &:nth-child(1) {
     border-color: #000;
     border-style: solid;
  }
}

.chart-wrap {
  width: 100%; position: relative;
  &::after {
      width: 100%;
      display: inline-block;
      border: 1px solid;
      border-radius: 5px;
      height: 28px;
      line-height: 28px;
      text-align: center;
  }
  &.literacy {
    &::after {
        content: '문해력';
        color: #4362CC;
        border-color: #4362CC;
    }
    .legend-wrap .legend-item p:nth-child(1) {
        background-color: var(--result-light-blue);
        &::after {background-color: var(--result-light-blue);  }
    }   
  }
  &.numeracy {
    &::after {
      content: '수리력';
      color: #CE4E56;
      border-color: #CE4E56;
    }
    .legend-wrap .legend-item p:nth-child(1) {
        background-color: var(--result-light-red);
        &::after {background-color: var(--result-light-red);  }
    }    
  }
}

.legend-wrap {
    display: flex;
    justify-content: center;
    gap: 10px;
    padding: 10px 0;
    .legend-item {
        display: flex;
        align-items: center;
        gap: 10px;
        p:nth-child(1) {
            width: 35px;
            height: 2px;
            border-radius: 2px;           
            position: relative;
            &::after {
                content: '';
                width: 10px;
                height: 10px;
                border-radius: 100%;
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%,-50%);               
            }
        }
        &:nth-child(2) p:nth-child(1) {
            background-color: #00A496 !important;
            &::after {background-color: #00A496 !important;}
        }
    }
}
</style>